import {Location} from '@angular/common';
import {Component, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {CoreConfigService} from '@core/services/config.service';
import {Store} from '@ngrx/store';
import {AuthHelperService} from 'app/auth/helpers/auth-helper.service';
import {login, stayLoggedIn} from 'app/store/actions/auth.action';
import {of, Subject, Subscription} from 'rxjs';
import {delay, take, takeUntil} from 'rxjs/operators';
import {CoreConfig} from '../../../../../@core/types';
import {GlobalService} from 'app/global/global.service';
import { LocalStorageKeys } from 'app/global/storage.service';

export interface ILoginForm {
    email: string;
    password: string;
    remember_me?: boolean;
}

@Component({
    selector: 'app-auth-login',
    templateUrl: './auth-login.component.html',
    styleUrls: ['./auth-login.component.scss'],
    encapsulation: ViewEncapsulation.None
})
export class AuthLoginV2Component implements OnInit, OnDestroy {
    //  Public
    public coreConfig: CoreConfig;
    public loginForm: UntypedFormGroup;
    public loading = false;
    public submitted = false;
    public returnUrl: string;
    public error = '';
    public passwordTextType: boolean;

    // Private
    private _unsubscribeAll: Subject<void>;
    private timeOutSubscription: Subscription;
    private retryIntervalId?: ReturnType<typeof setInterval>;
    private broadcastChannel: BroadcastChannel | null = null;
    private ssListener = (ev) => {
        const expiresAt = ev?.detail?.expiresAt ?? null;
        this.handleIncomingExpires(expiresAt);
    };
    private retryExpiresAt: number | null = null;
    // retry-related
    public retryRemainingSeconds = 0; // live countdown

    /**
     * Constructor
     *
     * @param {CoreConfigService} _coreConfigService
     * @param _formBuilder
     * @param store
     * @param authHelper
     * @param _location
     * @param _route
     * @param _globalService
     */
    constructor(
        private _coreConfigService: CoreConfigService,
        private _formBuilder: UntypedFormBuilder,
        private store: Store,
        private authHelper: AuthHelperService,
        private _location: Location,
        private _route: ActivatedRoute,
        private _globalService: GlobalService
    ) {
        this._unsubscribeAll = new Subject<void>();
        this.authHelper.setLayoutConfig(true, false, true);
    }

    // convenience getter for easy access to form fields
    get form() {
        return this.loginForm.controls;
    }

    /**
     * Toggle password
     */
    togglePasswordTextType() {
        this.passwordTextType = !this.passwordTextType;
    }

    onLogin() {
        // Block attempts while retry countdown running
        if (this.retryRemainingSeconds > 0) {
            return;
        }
        localStorage.setItem('lastLoginMethod', 'password');
        this.submitted = true;

        // stop here if form is invalid
        if (this.loginForm.invalid) {
            return;
        }
        this.loading = true;
        this.store.dispatch(login({
                credentials: {
                    email: this.loginForm.value.email,
                    password: this.loginForm.value.password,
                    remember_me: this.loginForm.value.remember_me
                },
                end_user: true,
                isThirdPartyLogin: false
            }
        ));
        this.store.dispatch(stayLoggedIn({stayLoggedIn: this.loginForm.value.remember_me}))
        this.timeOutSubscription = of(null).pipe(
            delay(500),
            take(1),
        ).subscribe(() => {
            this.loading = false;
        });
    }

    // Lifecycle Hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Check for OAuth error in query params
        const errorMessage = this._route.snapshot.queryParamMap.get('error');
        if (errorMessage) {
            this._globalService.toastrError(errorMessage);
        }

        this._location.replaceState('/login'); // To remove crm params and query params
        this.loginForm = this._formBuilder.group({
            email: ['', [Validators.required, Validators.email]],
            password: ['', Validators.required],
            remember_me: [false]
        });
        // Subscribe to config changes
        this._coreConfigService.config
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(config => {
                this.coreConfig = config;
            });
        // Restore retry countdown if any
        this.restoreRetryCountdown();

        // Setup BroadcastChannel if available for cross-tab and same-tab notifications
        try {
            if (typeof BroadcastChannel !== 'undefined') {
                this.broadcastChannel = new BroadcastChannel('ss-login-retry');
                this.broadcastChannel.onmessage = (ev) => {
                    const expiresAt = ev?.data?.expiresAt ?? null;
                    this.handleIncomingExpires(expiresAt);
                };
            }
        } catch (e) {
            // ignore
        }

        // CustomEvent fallback for same-tab immediate notification
        window.addEventListener('ss:login-retry', this.ssListener);
    }

/**
     * Handle incoming expiresAt from BroadcastChannel / CustomEvent.
     * Uses expiresAt as single source of truth so timer stays accurate even if
     * the browser throttles setInterval in background tabs.
     */
    private handleIncomingExpires(expiresAt: number | null) {
        if (!expiresAt) {
            // fallback: try reading persisted value
            this.restoreRetryCountdown();
            return;
        }

        // If we already have a later expiry, keep it (do not shorten)
        if (this.retryExpiresAt && this.retryExpiresAt > expiresAt) {
            return;
        }

        // start timer based on expiresAt
        this.startRetryTimer(expiresAt);
    }

    private restoreRetryCountdown() {
        // read persisted expiresAt (ms)
        const raw = localStorage.getItem(LocalStorageKeys.LOGIN_RETRY_EXPIRES_AT);
        if (!raw) {
            // make sure timer cleared
            this.clearRetryTimer();
            return;
        }

        const expiresAt = parseInt(raw, 10);
        if (isNaN(expiresAt) || expiresAt <= Date.now()) {
            // expired or invalid
            localStorage.removeItem(LocalStorageKeys.LOGIN_RETRY_EXPIRES_AT);
            this.clearRetryTimer();
            return;
        }

        // start timer with the persisted expiry
        this.startRetryTimer(expiresAt);
    }

    /**
     * Start a timer driven by expiresAt (ms). Each tick recalculates remaining
     * seconds from Date.now() to avoid inaccuracies when the tab is throttled.
     */
    private startRetryTimer(expiresAt: number) {
        // keep the longest block (do not shorten an existing later expiry)
        if (this.retryExpiresAt && this.retryExpiresAt > expiresAt) {
            return;
        }

        this.retryExpiresAt = expiresAt;
        // Compute immediate remaining seconds
        const computeRemaining = () => Math.max(0, Math.ceil((this.retryExpiresAt! - Date.now()) / 1000));
        const remainingNow = computeRemaining();

        if (remainingNow <= 0) {
            // already expired
            this.clearRetryTimer();
            localStorage.removeItem(LocalStorageKeys.LOGIN_RETRY_EXPIRES_AT);
            return;
        }

        // set live number immediately
        this.retryRemainingSeconds = remainingNow;

        // clear any existing interval before starting a fresh one
        if (this.retryIntervalId) {
            clearInterval(this.retryIntervalId);
            this.retryIntervalId = null;
        }
        // Tick every 1s but compute remaining from expiresAt each time
        this.retryIntervalId = setInterval(() => {
            // If expiresAt was cleared elsewhere, stop
            if (!this.retryExpiresAt) {
                this.clearRetryTimer();
                return;
            }
            const rem = computeRemaining();
            if (rem <= 0) {
                this.clearRetryTimer();
                try { localStorage.removeItem(LocalStorageKeys.LOGIN_RETRY_EXPIRES_AT); } catch (e) {
                    console.warn('Failed to remove login retry expiry from localStorage', e);
                }
                return;
            }
            this.retryRemainingSeconds = rem;
        }, 1000);
    }

    private clearRetryTimer() {
        if (this.retryIntervalId) {
            clearInterval(this.retryIntervalId);
            this.retryIntervalId = null;
        }
        this.retryRemainingSeconds = 0;
        this.retryExpiresAt = null;
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next();
        this._unsubscribeAll.complete();

        if (this.timeOutSubscription) {
            this.timeOutSubscription.unsubscribe();
        }

       // Clear any running retry timer
        this.clearRetryTimer();

        // Cleanup broadcast / event listeners
        if (this.broadcastChannel) {
            try { this.broadcastChannel.close(); } catch (e) { /* empty */ }
            this.broadcastChannel = null;
        }
        window.removeEventListener('ss:login-retry', this.ssListener);
    }
}
